import React, { useState, useEffect, useMemo, useCallback } from 'react'
import * as d3 from 'd3'
import { ServiceMap } from './components/ServiceMap'
import { ArchitectureDecisions } from './components/ArchitectureDecisions'
import { FeedbackAudit } from './components/FeedbackAudit'
import { AnalysisReview } from './components/AnalysisReview'
import { ChallengeAnalytics } from './components/ChallengeAnalytics'
import { Header } from './components/Header'
import { Sidebar } from './components/Sidebar'
import { useTheme } from './hooks/useTheme'
import { mockData } from './data/mockData'

const VIEWS = [
  { id: 'overview', label: 'Overview', icon: '📊' },
  { id: 'analyses', label: 'AI Analyses', icon: '🧠' },
  { id: 'challenges', label: 'Challenges', icon: '⚡' },
  { id: 'feedback', label: 'Feedback Audit', icon: '📋' },
  { id: 'services', label: 'Service Map', icon: '🔗' },
  { id: 'architecture', label: 'Architecture', icon: '🏗️' },
]

function App() {
  const [activeView, setActiveView] = useState('overview')
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const { theme, toggleTheme } = useTheme()
  const [data, setData] = useState(mockData)
  const [lastRefresh, setLastRefresh] = useState(new Date())

  const refreshData = useCallback(async () => {
    // In real app, fetch from API
    setLastRefresh(new Date())
  }, [])

  return (
    <div className="app" data-theme={theme}>
      <Header
        theme={theme}
        onToggleTheme={toggleTheme}
        onRefresh={refreshData}
        lastRefresh={lastRefresh}
        activeView={activeView}
      />
      <div className="app-body">
        <Sidebar
          open={sidebarOpen}
          onToggle={() => setSidebarOpen(!sidebarOpen)}
          activeView={activeView}
          onViewChange={setActiveView}
          views={VIEWS}
        />
        {/* The sidebar is a static flex child, so no margin-left is needed here --
            the old inline marginLeft doubled the offset (sidebar width + margin),
            pushing the content 260px too far right. */}
        <main className="main-content">
          <div className="view-container">
            {activeView === 'overview' && <OverviewDashboard data={data} />}
            {activeView === 'analyses' && <AnalysisReview />}
            {activeView === 'challenges' && <ChallengeAnalytics />}
            {activeView === 'feedback' && <FeedbackAudit />}
            {activeView === 'services' && <ServiceMap data={data.services.nodes} />}
            {activeView === 'architecture' && <ArchitectureDecisions data={data.architecture} />}
          </div>
        </main>
      </div>
      <style jsx>{`
        .app {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
        }
        .app-body {
          display: flex;
          flex: 1;
          overflow: hidden;
        }
        .main-content {
          flex: 1;
          overflow: auto;
          padding: 24px;
          transition: margin-left 0.2s ease;
        }
        .view-container {
          max-width: 1400px;
          margin: 0 auto;
        }
        @media (max-width: 1024px) {
          .main-content {
            padding: 16px;
          }
        }
      `}</style>
    </div>
  )
}

function OverviewDashboard({ data }) {
  const stats = useMemo(() => ({
    totalServices: data.services.nodes.length,
    healthyServices: data.services.nodes.filter(n => n.status === 'healthy').length,
    totalPipelines: data.pipeline.length,
    passingPipelines: data.pipeline.filter(p => p.status === 'passed').length,
    totalTests: data.coverage.summary.totalTests,
    passedTests: data.coverage.summary.passedTests,
    coveragePercent: data.coverage.summary.lineCoverage,
    securityIssues: data.security.findings.filter(f => f.severity === 'critical' || f.severity === 'high').length,
  }), [data])

  return (
    <div className="overview-dashboard">
      <div className="page-header">
        <h1>AI Agent Audit Dashboard</h1>
        <p className="page-subtitle">ESSMS/BMS Platform — Automated SDLC Pipeline Audit</p>
      </div>

      <div className="stats-grid" role="region" aria-label="Key metrics">
        <StatTile
          label="Total Services"
          value={stats.totalServices}
          subtitle={`${stats.healthyServices} healthy`}
          icon="🔧"
          trend={stats.healthyServices === stats.totalServices ? 'good' : 'warning'}
        />
        <StatTile
          label="Pipeline Health"
          value={`${stats.passingPipelines}/${stats.totalPipelines}`}
          subtitle={`${Math.round(stats.passingPipelines/stats.totalPipelines*100)}% passing`}
          icon="⚙️"
          trend={stats.passingPipelines === stats.totalPipelines ? 'good' : stats.passingPipelines > stats.totalPipelines*0.7 ? 'warning' : 'critical'}
        />
        <StatTile
          label="Test Coverage"
          value={`${stats.coveragePercent}%`}
          subtitle={`${stats.passedTests}/${stats.totalTests} tests passed`}
          icon="🧪"
          trend={stats.coveragePercent >= 80 ? 'good' : stats.coveragePercent >= 60 ? 'warning' : 'critical'}
        />
        <StatTile
          label="Security Issues"
          value={stats.securityIssues}
          subtitle="Critical + High severity"
          icon="🔒"
          trend={stats.securityIssues === 0 ? 'good' : stats.securityIssues <= 2 ? 'warning' : 'critical'}
        />
      </div>

      <div className="charts-grid">
        <section className="chart-card" aria-labelledby="service-health-title">
          <header className="chart-header">
            <h2 id="service-health-title">Service Health by Type</h2>
            <span className="chart-badge">Live</span>
          </header>
          <ServiceHealthChart data={data.services.nodes} />
        </section>

        <section className="chart-card" aria-labelledby="pipeline-trend-title">
          <header className="chart-header">
            <h2 id="pipeline-trend-title">Pipeline Success Rate (30 days)</h2>
            <span className="chart-badge">Trending</span>
          </header>
          <PipelineTrendChart data={data.pipelineHistory} />
        </section>

        <section className="chart-card" aria-labelledby="test-trend-title">
          <header className="chart-header">
            <h2 id="test-trend-title">Test Execution Trends</h2>
            <span className="chart-badge">Last 14 runs</span>
          </header>
          <TestTrendChart data={data.testHistory} />
        </section>

        <section className="chart-card" aria-labelledby="security-trend-title">
          <header className="chart-header">
            <h2 id="security-trend-title">Security Findings by Severity</h2>
            <span className="chart-badge">Current scan</span>
          </header>
          <SecuritySeverityChart findings={data.security.findings} />
        </section>
      </div>

      <div className="recent-activity">
        <h2>Recent AI Agent Activity</h2>
        <ActivityFeed activities={data.recentActivity} />
      </div>
      <style jsx>{`
        .overview-dashboard {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }
        .page-header {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .page-header h1 {
          font-size: 28px;
          font-weight: 600;
          color: var(--text-primary);
        }
        .page-subtitle {
          color: var(--text-secondary);
          font-size: 14px;
        }
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 16px;
        }
        .charts-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: 24px;
        }
        .chart-card {
          background: var(--surface-1);
          border: 1px solid var(--border);
          border-radius: 8px;
          overflow: hidden;
        }
        .chart-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 20px;
          border-bottom: 1px solid var(--border);
        }
        .chart-header h2 {
          font-size: 16px;
          font-weight: 600;
          color: var(--text-primary);
        }
        .chart-badge {
          font-size: 11px;
          font-weight: 600;
          padding: 2px 8px;
          border-radius: 999px;
          background: var(--sequential-100);
          color: var(--sequential-550);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        @media (prefers-color-scheme: dark) {
          :root:where(:not([data-theme="light"])) .chart-badge,
          [data-theme="dark"] .chart-badge {
            background: var(--sequential-700);
            color: var(--sequential-250);
          }
        }
        .chart-card > div {
          padding: 20px;
          height: 300px;
        }
        .recent-activity h2 {
          font-size: 18px;
          font-weight: 600;
          margin-bottom: 16px;
          color: var(--text-primary);
        }
        @media (max-width: 900px) {
          .charts-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  )
}

function StatTile({ label, value, subtitle, icon, trend }) {
  const trendColors = {
    good: 'var(--status-good)',
    warning: 'var(--status-warning)',
    critical: 'var(--status-critical)',
  }
  const trendIcons = {
    good: '✓',
    warning: '⚠',
    critical: '✕',
  }

  return (
    <article className="stat-tile" style={{ borderLeftColor: trendColors[trend] }}>
      <div className="stat-icon">{icon}</div>
      <div className="stat-content">
        <div className="stat-label">{label}</div>
        <div className="stat-value">{value}</div>
        <div className="stat-subtitle">{subtitle}</div>
      </div>
      <div className="stat-trend" style={{ color: trendColors[trend] }}>
        {trendIcons[trend]}
      </div>
      <style jsx>{`
        .stat-tile {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 20px;
          background: var(--surface-1);
          border: 1px solid var(--border);
          border-radius: 8px;
          border-left: 4px solid;
          transition: box-shadow 0.2s;
        }
        .stat-tile:hover {
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
        }
        .stat-icon {
          font-size: 28px;
          width: 56px;
          height: 56px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--page-plane);
          border-radius: 12px;
        }
        .stat-content {
          flex: 1;
          min-width: 0;
        }
        .stat-label {
          font-size: 12px;
          font-weight: 500;
          color: var(--text-muted);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 4px;
        }
        .stat-value {
          font-size: 28px;
          font-weight: 700;
          color: var(--text-primary);
          line-height: 1.2;
          font-variant-numeric: tabular-nums;
        }
        .stat-subtitle {
          font-size: 13px;
          color: var(--text-secondary);
          margin-top: 2px;
        }
        .stat-trend {
          font-size: 20px;
          font-weight: 600;
        }
      `}</style>
    </article>
  )
}

function ServiceHealthChart({ data }) {
  const containerRef = React.useRef(null)
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })

  useEffect(() => {
    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        setDimensions({ width: entry.contentRect.width, height: entry.contentRect.height })
      }
    })
    if (containerRef.current) resizeObserver.observe(containerRef.current)
    return () => resizeObserver.disconnect()
  }, [])

  const typeCounts = useMemo(() => {
    const counts = {}
    data.forEach(s => {
      counts[s.type] = (counts[s.type] || 0) + 1
    })
    return Object.entries(counts).map(([type, total]) => ({
      type,
      total,
      healthy: data.filter(s => s.type === type && s.status === 'healthy').length,
      degraded: data.filter(s => s.type === type && s.status === 'degraded').length,
      down: data.filter(s => s.type === type && s.status === 'down').length,
    }))
  }, [data])

  if (dimensions.width === 0) return <div ref={containerRef} style={{ height: '100%' }} />

  const margin = { top: 10, right: 120, bottom: 40, left: 10 }
  const width = dimensions.width - margin.left - margin.right
  const height = dimensions.height - margin.top - margin.bottom

  const x0 = d3.scaleBand().domain(typeCounts.map(d => d.type)).range([0, width]).paddingInner(0.2)
  const x1 = d3.scaleBand().domain(['healthy', 'degraded', 'down']).range([0, x0.bandwidth()]).padding(0.05)
  const y = d3.scaleLinear().domain([0, d3.max(typeCounts, d => d.total)]).nice().range([height, 0])

  const color = {
    healthy: 'var(--status-good)',
    degraded: 'var(--status-warning)',
    down: 'var(--status-critical)',
  }

  return (
    <svg className="viz-root" width={dimensions.width} height={dimensions.height} viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}>
      <g transform={`translate(${margin.left},${margin.top})`}>
        {/* Y axis */}
        <g className="axis">
          {y.ticks(5).map(tick => (
            <g key={tick} transform={`translate(0,${y(tick)})`}>
              <line x1={0} x2={width} stroke="var(--gridline)" strokeWidth={0.5} />
              <text x={-8} y={4} textAnchor="end" fontSize={11} fill="var(--text-muted)">{tick}</text>
            </g>
          ))}
          <line x1={0} y1={0} x2={0} y2={height} stroke="var(--baseline)" strokeWidth={1} />
        </g>

        {/* Bars */}
        <g>
          {typeCounts.map((d, i) => (
            <g key={d.type} transform={`translate(${x0(d.type)},0)`}>
              {['healthy', 'degraded', 'down'].map(status => (
                <rect
                  key={status}
                  x={x1(status)}
                  y={y(d[status])}
                  width={x1.bandwidth()}
                  height={height - y(d[status])}
                  fill={color[status]}
                  rx={2}
                />
              ))}
              <text x={x0.bandwidth()/2} y={height + 20} textAnchor="middle" fontSize={11} fill="var(--text-secondary)">
                {d.type}
              </text>
            </g>
          ))}
        </g>

        {/* Legend */}
        <g transform={`translate(${width + 20}, 0)`}>
          {['healthy', 'degraded', 'down'].map((status, i) => (
            <g key={status} transform={`translate(0, ${i * 24})`}>
              <rect x={0} y={0} width={16} height={16} rx={3} fill={color[status]} />
              <text x={22} y={12} fontSize={12} fill="var(--text-secondary)" dominantBaseline="middle">
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </text>
            </g>
          ))}
        </g>
      </g>
      <style jsx>{`
        .viz-root .axis text { font-family: inherit; }
        .viz-root .axis line { stroke: var(--baseline); }
      `}</style>
    </svg>
  )
}

function PipelineTrendChart({ data }) {
  const containerRef = React.useRef(null)
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })

  useEffect(() => {
    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) setDimensions({ width: entry.contentRect.width, height: entry.contentRect.height })
    })
    if (containerRef.current) resizeObserver.observe(containerRef.current)
    return () => resizeObserver.disconnect()
  }, [])

  if (dimensions.width === 0) return <div ref={containerRef} style={{ height: '100%' }} />

  const margin = { top: 10, right: 20, bottom: 40, left: 50 }
  const width = dimensions.width - margin.left - margin.right
  const height = dimensions.height - margin.top - margin.bottom

  const x = d3.scaleTime().domain(d3.extent(data, d => new Date(d.date))).range([0, width])
  const y = d3.scaleLinear().domain([0, 100]).range([height, 0])

  const line = d3.line()
    .x(d => x(new Date(d.date)))
    .y(d => y(d.successRate))
    .curve(d3.curveMonotoneX)

  return (
    <svg className="viz-root" width={dimensions.width} height={dimensions.height} viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}>
      <g transform={`translate(${margin.left},${margin.top})`}>
        {/* Grid */}
        <g className="grid">
          {y.ticks(5).slice(1).map(tick => (
            <line key={tick} x1={0} x2={width} y1={y(tick)} y2={y(tick)} stroke="var(--gridline)" strokeWidth={0.5} strokeDasharray="4,4" />
          ))}
        </g>

        {/* Axes */}
        <g className="axis">
          <line x1={0} y1={0} x2={0} y2={height} stroke="var(--baseline)" strokeWidth={1} />
          <line x1={0} y1={height} x2={width} y2={height} stroke="var(--baseline)" strokeWidth={1} />
          {y.ticks(5).map(tick => (
            <g key={tick} transform={`translate(0,${y(tick)})`}>
              <line x1={-6} x2={0} stroke="var(--baseline)" strokeWidth={1} />
              <text x={-10} y={4} textAnchor="end" fontSize={11} fill="var(--text-muted)">{tick}%</text>
            </g>
          ))}
          {x.ticks(6).map(tick => (
            <g key={tick} transform={`translate(${x(tick)},${height})`}>
              <line x1={0} x2={0} y1={0} y2={6} stroke="var(--baseline)" strokeWidth={1} />
              <text x={0} y={18} textAnchor="middle" fontSize={11} fill="var(--text-muted)">{d3.timeFormat('%b %d')(tick)}</text>
            </g>
          ))}
        </g>

        {/* Line */}
        <path
          d={line(data)}
          fill="none"
          stroke="var(--series-1)"
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Points */}
        <g>
          {data.map((d, i) => (
            <circle
              key={i}
              cx={x(new Date(d.date))}
              cy={y(d.successRate)}
              r={4}
              fill="var(--series-1)"
              stroke="var(--surface-1)"
              strokeWidth={2}
            />
          ))}
        </g>

        {/* Target line */}
        <line
          x1={0} x2={width}
          y1={y(80)} y2={y(80)}
          stroke="var(--status-good)"
          strokeWidth={1.5}
          strokeDasharray="6,4"
          opacity={0.7}
        />
        <text x={width - 60} y={y(80) - 8} fontSize={10} fill="var(--status-good)" textAnchor="end">Target 80%</text>
      </g>
      <style jsx>{`
        .viz-root .axis text { font-family: inherit; }
      `}</style>
    </svg>
  )
}

function TestTrendChart({ data }) {
  const containerRef = React.useRef(null)
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })

  useEffect(() => {
    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) setDimensions({ width: entry.contentRect.width, height: entry.contentRect.height })
    })
    if (containerRef.current) resizeObserver.observe(containerRef.current)
    return () => resizeObserver.disconnect()
  }, [])

  if (dimensions.width === 0) return <div ref={containerRef} style={{ height: '100%' }} />

  const margin = { top: 10, right: 20, bottom: 40, left: 50 }
  const width = dimensions.width - margin.left - margin.right
  const height = dimensions.height - margin.top - margin.bottom

  const maxTests = d3.max(data, d => Math.max(d.passed, d.failed, d.skipped))

  const x = d3.scaleBand().domain(data.map((_, i) => `Run ${i+1}`)).range([0, width]).padding(0.3)
  const y = d3.scaleLinear().domain([0, maxTests * 1.1]).nice().range([height, 0])

  return (
    <svg className="viz-root" width={dimensions.width} height={dimensions.height} viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}>
      <g transform={`translate(${margin.left},${margin.top})`}>
        {/* Grid */}
        <g className="grid">
          {y.ticks(5).slice(1).map(tick => (
            <line key={tick} x1={0} x2={width} y1={y(tick)} y2={y(tick)} stroke="var(--gridline)" strokeWidth={0.5} strokeDasharray="4,4" />
          ))}
        </g>

        {/* Axes */}
        <g className="axis">
          <line x1={0} y1={0} x2={0} y2={height} stroke="var(--baseline)" strokeWidth={1} />
          <line x1={0} y1={height} x2={width} y2={height} stroke="var(--baseline)" strokeWidth={1} />
          {y.ticks(5).map(tick => (
            <g key={tick} transform={`translate(0,${y(tick)})`}>
              <line x1={-6} x2={0} stroke="var(--baseline)" strokeWidth={1} />
              <text x={-10} y={4} textAnchor="end" fontSize={11} fill="var(--text-muted)">{tick}</text>
            </g>
          ))}
          {data.map((_, i) => (
            <g key={i} transform={`translate(${x(`Run ${i+1}`) + x.bandwidth()/2},${height})`}>
              <line x1={0} x2={0} y1={0} y2={6} stroke="var(--baseline)" strokeWidth={1} />
              <text x={0} y={18} textAnchor="middle" fontSize={11} fill="var(--text-muted)">{i+1}</text>
            </g>
          ))}
        </g>

        {/* Stacked bars */}
        <g>
          {data.map((d, i) => (
            <g key={i} transform={`translate(${x(`Run ${i+1}`)},0)`}>
              <rect x={2} y={y(d.passed)} width={x.bandwidth()-4} height={y(0) - y(d.passed)} fill="var(--status-good)" rx={2} />
              <rect x={2} y={y(d.passed + d.failed)} width={x.bandwidth()-4} height={y(d.passed) - y(d.passed + d.failed)} fill="var(--status-critical)" rx={2} />
              <rect x={2} y={y(d.passed + d.failed + d.skipped)} width={x.bandwidth()-4} height={y(d.passed + d.failed) - y(d.passed + d.failed + d.skipped)} fill="var(--status-warning)" rx={2} opacity={0.7} />
            </g>
          ))}
        </g>

        {/* Legend */}
        <g transform={`translate(${width - 180}, -30)`}>
          {[
            { label: 'Passed', color: 'var(--status-good)' },
            { label: 'Failed', color: 'var(--status-critical)' },
            { label: 'Skipped', color: 'var(--status-warning)' },
          ].map((item, i) => (
            <g key={item.label} transform={`translate(${i * 60}, 0)`}>
              <rect x={0} y={0} width={12} height={12} rx={2} fill={item.color} />
              <text x={16} y={9} fontSize={11} fill="var(--text-secondary)">{item.label}</text>
            </g>
          ))}
        </g>
      </g>
    </svg>
  )
}

function SecuritySeverityChart({ findings }) {
  const containerRef = React.useRef(null)
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })

  useEffect(() => {
    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) setDimensions({ width: entry.contentRect.width, height: entry.contentRect.height })
    })
    if (containerRef.current) resizeObserver.observe(containerRef.current)
    return () => resizeObserver.disconnect()
  }, [])

  if (dimensions.width === 0) return <div ref={containerRef} style={{ height: '100%' }} />

  const severityOrder = ['critical', 'high', 'medium', 'low', 'info']
  const severityColors = {
    critical: 'var(--status-critical)',
    high: 'var(--status-serious)',
    medium: 'var(--status-warning)',
    low: 'var(--series-1)',
    info: 'var(--text-muted)',
  }

  const counts = severityOrder.map(s => ({
    severity: s,
    count: findings.filter(f => f.severity === s).length,
    color: severityColors[s],
  })).filter(d => d.count > 0)

  const margin = { top: 20, right: 20, bottom: 20, left: 20 }
  const width = dimensions.width - margin.left - margin.right
  const height = dimensions.height - margin.top - margin.bottom
  const radius = Math.min(width, height) / 2 - 20

  const pie = d3.pie().value(d => d.count).sort(null)
  const arc = d3.arc().innerRadius(radius * 0.5).outerRadius(radius)
  const labelArc = d3.arc().innerRadius(radius * 0.7).outerRadius(radius * 0.7)

  return (
    <svg className="viz-root" width={dimensions.width} height={dimensions.height} viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}>
      <g transform={`translate(${margin.left + width/2},${margin.top + height/2})`}>
        {pie(counts).map((slice, i) => (
          <g key={slice.data.severity}>
            <path
              d={arc(slice)}
              fill={slice.data.color}
              stroke="var(--surface-1)"
              strokeWidth={2}
            />
            {slice.data.count > 0 && (
              <text
                transform={`translate(${labelArc.centroid(slice)})`}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize={12}
                fontWeight={600}
                fill="var(--surface-1)"
              >
                {slice.data.count}
              </text>
            )}
          </g>
        ))}
        {/* Legend */}
        <g transform={`translate(${-width/2 + 10}, ${-height/2 + 20})`}>
          {counts.map((d, i) => (
            <g key={d.severity} transform={`translate(0, ${i * 22})`}>
              <rect x={0} y={0} width={14} height={14} rx={3} fill={d.color} />
              <text x={20} y={10} fontSize={12} fill="var(--text-secondary)" dominantBaseline="middle">
                {d.severity.charAt(0).toUpperCase() + d.severity.slice(1)} ({d.count})
              </text>
            </g>
          ))}
        </g>
      </g>
    </svg>
  )
}

function ActivityFeed({ activities }) {
  return (
    <div className="activity-feed" role="feed" aria-label="Recent activity">
      {activities.slice(0, 10).map((activity, i) => (
        <article key={i} className="activity-item">
          <div className="activity-time">
            <time dateTime={activity.timestamp}>{new Date(activity.timestamp).toLocaleString()}</time>
          </div>
          <div className="activity-indicator" style={{ background: activity.type === 'success' ? 'var(--status-good)' : activity.type === 'warning' ? 'var(--status-warning)' : 'var(--series-1)' }} />
          <div className="activity-content">
            <div className="activity-title">{activity.title}</div>
            <div className="activity-description">{activity.description}</div>
            <div className="activity-meta">
              <span className="activity-agent">Agent: {activity.agent}</span>
              <span className="activity-duration">{activity.duration}</span>
            </div>
          </div>
        </article>
      ))}
      <style jsx>{`
        .activity-feed {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .activity-item {
          display: flex;
          gap: 12px;
          padding: 12px 16px;
          background: var(--surface-1);
          border: 1px solid var(--border);
          border-radius: 8px;
          transition: box-shadow 0.2s;
        }
        .activity-item:hover {
          box-shadow: 0 2px 8px rgba(0,0,0,0.06);
        }
        .activity-time {
          min-width: 150px;
          fontSize: 12px;
          color: var(--text-muted);
          white-space: nowrap;
        }
        .activity-indicator {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          margin-top: 6px;
          flex-shrink: 0;
        }
        .activity-content {
          flex: 1;
          min-width: 0;
        }
        .activity-title {
          font-weight: 500;
          color: var(--text-primary);
          margin-bottom: 2px;
        }
        .activity-description {
          font-size: 13px;
          color: var(--text-secondary);
          margin-bottom: 6px;
        }
        .activity-meta {
          display: flex;
          gap: 16px;
          font-size: 11px;
          color: var(--text-muted);
        }
      `}</style>
    </div>
  )
}

export default App